import { useEffect, useState, type KeyboardEvent } from 'react';

/**
 * Keyboard movement through a list driven from a text input (combobox, command palette):
 * ↑/↓ move (wrapping), Home/End jump, Enter picks. The active item goes back to `initialIndex`
 * (default the first) whenever `resetKey` changes (e.g. the query), and is scrolled into view via
 * its element id.
 */
export function useListNavigation({
  count,
  onPick,
  idOf,
  resetKey,
  initialIndex = 0,
}: {
  count: number;
  onPick(index: number): void;
  idOf(index: number): string;
  resetKey?: unknown;
  initialIndex?: number;
}) {
  const [active, setActive] = useState(initialIndex);
  const [lastReset, setLastReset] = useState(resetKey);
  if (resetKey !== lastReset) {
    setLastReset(resetKey);
    setActive(initialIndex);
  }
  const current = count === 0 ? -1 : Math.min(active, count - 1);

  useEffect(() => {
    if (current >= 0) document.getElementById(idOf(current))?.scrollIntoView?.({ block: 'nearest' });
  }, [current, idOf]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (count === 0) return;
    const moves: Record<string, () => number> = {
      ArrowDown: () => (current + 1) % count,
      ArrowUp: () => (current - 1 + count) % count,
      Home: () => 0,
      End: () => count - 1,
    };
    const move = moves[e.key];
    if (move) {
      e.preventDefault();
      setActive(move());
    } else if (e.key === 'Enter' && current >= 0) {
      e.preventDefault();
      onPick(current);
    }
  };

  return {
    /** Index of the highlighted item, -1 when the list is empty. */
    active: current,
    setActive,
    onKeyDown,
    /** Value for the input's aria-activedescendant. */
    activeId: current >= 0 ? idOf(current) : undefined,
  };
}
